"use client";

import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { PartialRecord } from "@huub-web/utils";
import { type Timeout, usePubSub, useDebug } from "@huub-web/ui";

import {
  WebSocketContext,
  type WebSocketDeregisterListenerCallback,
  type WebSocketListenerHandler,
  type WebSocketRegisterListenerCallback,
} from "../../contexts";
import {
  WebSocketChannel,
  type WebSocketChatMessage,
  type WebSocketMessage,
} from "../../types";
import { useAuth } from "../../hooks";
import { parseProviderMismatchFromCloseReason } from "../../types";
import {
  createVisualDebug,
  formatChannelLog,
  createOutgoingMessageDebugBody,
  createIncomingMessageDebugBody,
  DEBUG_EXCLUDED_CHANNELS,
  DEBUG_VISUAL_INCOMING_CHANNELS,
  DEBUG_VISUAL_OUTGOING_CHANNELS,
  DEBUG_CONSOLE_EXCLUDED_CHANNELS,
} from "./WebSocketProvider.debug";

const MAX_RECONNECT_ATTEMPTS = 3;
const MAX_RECURRENT_CLOSE_ATTEMPTS = 4;
const RECONNECT_DELAY = 2000;
const CLOSE_RESET_DELAY = 20 * 1000;

/**
 * WebSocket subprotocol scheme identifier used to authenticate the connection.
 *
 * The id_token used to be passed as a `?id_token=<JWT>` query-string parameter,
 * which leaks the session credential into reverse-proxy / load-balancer /
 * server access logs, APM traces, browser history and Referer headers
 * (SMA-1484 / SMA-3167 / SMA-3394). The browser `WebSocket` constructor cannot
 * set arbitrary request headers, but it CAN send subprotocols via the
 * `Sec-WebSocket-Protocol` request header (the constructor's second argument),
 * which is not written to URL-based access logs.
 *
 * Contract with the huub backend (coordinated, backend fixed in a separate
 * lane): the client sends two subprotocol values on connect —
 *   1. this scheme identifier, and
 *   2. the raw id_token JWT.
 * The backend reads the JWT from the `Sec-WebSocket-Protocol` header (the value
 * following {@link WS_AUTH_SUBPROTOCOL}) instead of the query string, and MUST
 * echo back the accepted subprotocol (the scheme identifier) when accepting the
 * connection so the browser's subprotocol negotiation succeeds.
 *
 * A raw JWT is `base64url` segments joined by `.`; every character is within the
 * RFC 6455 subprotocol token charset, so no additional encoding is required.
 */
const WS_AUTH_SUBPROTOCOL = "huub.auth.id-token";

export type WebSocketProviderProps = {
  baseEndpoint: string;
  children: ReactNode;
  params?: Record<string, string>;
  onFailure?: () => void;
  debug?: boolean;
};

/**
 * WebSocketProvider component that provides a WebSocket connection to the application and handles the connection state.
 */
export const WebSocketProvider: FC<WebSocketProviderProps> = ({
  baseEndpoint,
  params = {},
  debug = false,
  children,
  onFailure,
}) => {
  const { identityToken, handleProviderMismatch } = useAuth();
  const pubSub = usePubSub();
  const { log } = useDebug();

  const webSocketRef = useRef<WebSocket | null>(null);
  const isInitialized = useRef(false);
  const isConnecting = useRef(false);
  const reconnectAttempts = useRef<number>(0);
  const closeAttempts = useRef<number>(0);
  const resetCloseAttemptsTimeout = useRef<Timeout | null>(null);

  // Store debug and pubSub in refs so handlers can access current values without recreating
  const debugRef = useRef(debug);
  const pubSubRef = useRef(pubSub);
  const onFailureRef = useRef(onFailure);
  const handleProviderMismatchRef = useRef(handleProviderMismatch);

  // Update refs whenever props change
  useEffect(() => {
    debugRef.current = debug;
    pubSubRef.current = pubSub;
    onFailureRef.current = onFailure;
    handleProviderMismatchRef.current = handleProviderMismatch;
  }, [debug, pubSub, onFailure, handleProviderMismatch]);

  const [isConnected, setConnected] = useState(false);
  const [isWaitingForAgentResponse, setWaitingForAgentResponse] =
    useState(false);
  const [hasConnectionFailed, setConnectionFailed] = useState(false);

  const listeners = useRef<
    PartialRecord<
      WebSocketChannel,
      Record<string, WebSocketListenerHandler<WebSocketMessage>>
    >
  >({});

  const visualDebug = useCallback(
    (
      title: string | string[],
      description: ReactNode = "",
      variant: "success" | "error" | "warning" = "warning",
    ) => {
      const currentDebug = debugRef.current;
      const currentPubSub = pubSubRef.current;
      const visualDebugFn = createVisualDebug(currentDebug, currentPubSub);
      visualDebugFn(title, description, variant);
    },
    [],
  );

  const registerListener: WebSocketRegisterListenerCallback = useCallback(
    (channel, id, handler) => {
      listeners.current[channel as WebSocketChannel] = {
        ...listeners.current[channel as WebSocketChannel],
        [id]: handler as WebSocketListenerHandler<WebSocketMessage>,
      };
    },
    [],
  );

  const deregisterListener: WebSocketDeregisterListenerCallback = useCallback(
    (channel, id) => {
      delete listeners.current?.[channel as WebSocketChannel]?.[id];
    },
    [],
  );

  const sendMessage = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (channel: WebSocketChannel, data: any = {}) => {
      if (
        webSocketRef.current &&
        webSocketRef.current.readyState === WebSocket.OPEN
      ) {
        // If the channel is voice data, we can assume that the data is a blob
        if (channel === WebSocketChannel.VOICE_DATA) {
          webSocketRef.current.send(data);

          log(
            "yellow",
            ["WebSocket", formatChannelLog(channel, "out")],
            "Sending voice blob",
          );
        } else {
          // Otherwise, we can assume that the data is a JSON object
          const socketMessage = { type: channel, data };
          const socketMessagePayload = JSON.stringify(socketMessage);

          webSocketRef.current.send(socketMessagePayload);

          // If the channel is a chat message, we can assume that the message is a question,
          // or chat initiate, and we are waiting for an answer
          if (
            channel === WebSocketChannel.CHAT ||
            channel === WebSocketChannel.CHAT_INITIATE
          ) {
            setWaitingForAgentResponse(true);
          }

          // Log the message
          log(
            "yellow",
            ["WebSocket", formatChannelLog(channel, "out")],
            "Sending message",
            socketMessage,
          );

          // Debug the message (read current debug value from ref)
          const currentDebug = debugRef.current;
          if (
            !DEBUG_EXCLUDED_CHANNELS.includes(channel) &&
            DEBUG_VISUAL_OUTGOING_CHANNELS.includes(channel) &&
            currentDebug
          ) {
            const body = createOutgoingMessageDebugBody(channel, socketMessage);
            visualDebug(formatChannelLog(channel, "out"), body);
          }
        }
      } else {
        log(
          "yellow",
          ["WebSocket", formatChannelLog(channel)],
          "⚠️ WebSocket is not open, cannot send message",
        );

        visualDebug(
          formatChannelLog(channel),
          "WebSocket is not open, cannot send message",
          "warning",
        );
      }
    },
    [visualDebug, log],
  );

  const handleSocketOpen = useCallback(() => {
    isConnecting.current = false;
    reconnectAttempts.current = 0;
    setConnected(true);
    setConnectionFailed(false);

    log("yellow", "WebSocket", "Connected to WebSocket");
    visualDebug("Connected", "", "success");

    if (resetCloseAttemptsTimeout.current) {
      clearTimeout(resetCloseAttemptsTimeout.current);
    }
    resetCloseAttemptsTimeout.current = setTimeout(() => {
      setConnectionFailed((failed) => {
        if (!failed) {
          closeAttempts.current = 0;
        }
        return failed;
      });
    }, CLOSE_RESET_DELAY);

    sendMessage(WebSocketChannel.PING);
  }, [log, visualDebug, sendMessage]);

  const connectRef = useRef<(() => void) | null>(null);

  const handleSocketClose = useCallback(
    (event: CloseEvent) => {
      // Check for provider mismatch before attempting reconnection
      // Backend sends close code 3000 with "Provider mismatch" in reason
      if (event.reason) {
        const providerMismatchError = parseProviderMismatchFromCloseReason(
          event.reason,
        );

        if (providerMismatchError) {
          log(
            "yellow",
            "WebSocket",
            "Provider mismatch detected - logging out",
            event.reason,
          );
          visualDebug("Provider mismatch", event.reason, "error");
          handleProviderMismatchRef.current?.(providerMismatchError);
          return;
        }
      }

      closeAttempts.current = closeAttempts.current + 1;
      isConnecting.current = false;
      setConnected(false);

      log("yellow", "WebSocket", "Disconnected from WebSocket");
      visualDebug("Disconnected", "", "error");

      if (closeAttempts.current > MAX_RECURRENT_CLOSE_ATTEMPTS) {
        log("yellow", "WebSocket", "Max. close attempts reached");
        visualDebug("Max. attempts reached", "error");

        setConnectionFailed(true);

        if (resetCloseAttemptsTimeout.current) {
          clearTimeout(resetCloseAttemptsTimeout.current);
        }
      } else if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current = reconnectAttempts.current + 1;

        log(
          "yellow",
          "WebSocket",
          `Attempting to reconnect... (${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`,
        );
        visualDebug(
          "Reconnecting",
          `Attempting to reconnect... (${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`,
          "error",
        );

        setTimeout(() => connectRef.current?.(), RECONNECT_DELAY);
      } else {
        log("yellow", "WebSocket", "Max. reconnection attempts reached");
        visualDebug([], "Max. reconnection attempts reached", "error");

        onFailureRef.current?.();

        setConnectionFailed(true);
      }
    },
    [log, visualDebug],
  );

  const handleSocketMessage = useCallback(
    (event: MessageEvent) => {
      const message: WebSocketMessage =
        event.data instanceof Blob
          ? { type: WebSocketChannel.AUDIO_BLOB, data: event.data }
          : JSON.parse(event.data);

      const channel: WebSocketChannel = message.type as WebSocketChannel;

      // Read current debug value from ref
      const currentDebug = debugRef.current;

      // We only debug certain messages
      if (!DEBUG_CONSOLE_EXCLUDED_CHANNELS.includes(channel)) {
        log(
          "yellow",
          ["WebSocket", formatChannelLog(channel, "in")],
          "Received message",
          message.data,
        );

        // Visual debug only certain messages
        if (DEBUG_VISUAL_INCOMING_CHANNELS.includes(channel) && currentDebug) {
          // Skip visual debug for streaming messages, but still process them
          const isStreamingChat =
            channel === WebSocketChannel.CHAT &&
            (message as WebSocketChatMessage)?.data?.streaming === true;

          if (!isStreamingChat) {
            const body = createIncomingMessageDebugBody(channel, message);
            visualDebug([formatChannelLog(channel, "in")], body);
          }
        }
      }

      if (!Object.values(WebSocketChannel).includes(channel)) {
        console.warn(`Unknown message channel: ${channel}`);
        return;
      }

      // Handle chat message by emitting the message to the listeners
      const handle = () => {
        // If the message is a chat message, and it is not streaming, we can assume that the chat message is an
        // completed answer to a question
        if (channel === WebSocketChannel.CHAT) {
          if ((message as WebSocketChatMessage)?.data?.streaming !== true) {
            setWaitingForAgentResponse(false);
          } else if (
            (message as WebSocketChatMessage)?.data?.streaming === true
          ) {
            // We ensure that we are waiting for an agent response, in case multiple messages are still
            //  streaming, but are ignored or filtered out by the navigation context
            setWaitingForAgentResponse(true);
          }
        }
        if (channel === WebSocketChannel.VOICE_FULL_TRANSCRIPT) {
          setWaitingForAgentResponse(true);
        }

        // Emit the message to all listeners
        Object.values(listeners.current?.[channel] ?? {}).forEach((handler) => {
          handler(message);
        });
      };

      handle();
    },
    [log, visualDebug],
  );

  const connect = useCallback(() => {
    if (
      webSocketRef.current &&
      webSocketRef.current.readyState === WebSocket.OPEN
    ) {
      log("yellow", "WebSocket", "WebSocket is already open");
      visualDebug([], "WebSocket is already open");
      return;
    }

    if (isConnecting.current) {
      log("yellow", "WebSocket", "Connection already in progress");
      visualDebug([], "Connection already in progress");
      return;
    }

    isConnecting.current = true;
    log("yellow", "WebSocket", "Setting up WebSocket", params);

    // Non-sensitive connection params (e.g. tts_enabled) stay in the query
    // string; the id_token is sent via the Sec-WebSocket-Protocol header
    // instead of the URL to keep the session credential out of access logs
    // (SMA-1484 / SMA-3167 / SMA-3394).
    const query = new URLSearchParams(params).toString();
    const url = query ? `${baseEndpoint}?${query}` : baseEndpoint;

    // Authenticate via subprotocol: [scheme identifier, raw id_token JWT].
    // See WS_AUTH_SUBPROTOCOL for the backend contract.
    //
    // The constructor can throw synchronously — e.g. a malformed token that
    // contains characters outside the RFC 6455 subprotocol token set. Reset the
    // in-progress flag on failure so a later `connect` can recover instead of
    // sticking forever behind the `isConnecting` guard.
    let socket: WebSocket;
    try {
      socket = identityToken
        ? new WebSocket(url, [WS_AUTH_SUBPROTOCOL, identityToken])
        : new WebSocket(url);
    } catch (error) {
      // A synchronous constructor throw (e.g. a malformed token outside the
      // RFC 6455 subprotocol token set) will recur on every retry, so treat it
      // as a terminal failure: reset the in-progress flag and signal failure so
      // the UI can react instead of hanging with no realtime recovery path.
      isConnecting.current = false;
      webSocketRef.current = null;
      setConnected(false);
      log("red", "WebSocket", "Failed to open WebSocket", error);
      visualDebug([], "Failed to open WebSocket", "error");
      onFailureRef.current?.();
      setConnectionFailed(true);
      return;
    }
    webSocketRef.current = socket;

    // Handlers are now stable - no need to recreate or reassign them
    socket.onopen = handleSocketOpen;
    socket.onclose = handleSocketClose;
    socket.onmessage = handleSocketMessage;
  }, [
    baseEndpoint,
    params,
    identityToken,
    log,
    visualDebug,
    handleSocketOpen,
    handleSocketClose,
    handleSocketMessage,
  ]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    if (!identityToken) {
      if (!webSocketRef.current) {
        return;
      }

      webSocketRef.current?.close();
      webSocketRef.current = null;
      return;
    }

    if (!isInitialized.current) {
      setTimeout(connect, 500);
      isInitialized.current = true;
    }

    return () => {
      if (!webSocketRef.current) {
        return;
      }

      log("yellow", "WebSocket", "Closing WebSocket by destructor");
      visualDebug([], "Closing WebSocket by destructor", "warning");
      console.warn("Closing WebSocket by destructor");

      webSocketRef.current?.close();
      webSocketRef.current = null;
    };
  }, [identityToken]);

  const stopWaiting = () => {
    setWaitingForAgentResponse(false);
  };

  useEffect(() => {
    const unsubscribeMockWebSocketMessage = pubSub.subscribe(
      "mockWebSocketMessage",
      ({ event }) => {
        log("yellow", "WebSocket", "Mocking WebSocket message", event);
        handleSocketMessage(event);
      },
    );

    const unsubscribeManualWaitingForChatAnswer = pubSub.subscribe(
      "manualWaitingForChatAnswer",
      () => {
        log("yellow", "WebSocket", "Manually setting waiting for chat answer");
        setWaitingForAgentResponse(true);
      },
    );

    return () => {
      unsubscribeMockWebSocketMessage();
      unsubscribeManualWaitingForChatAnswer();
    };
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        hasConnectionFailed,
        isWaitingForAgentResponse,
        stopWaiting,
        sendMessage,
        registerListener,
        deregisterListener,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
